/**
 * Helpers for the Map<string, SceneObject> store structure.
 *
 * These replace the old array-based patterns:
 *   sceneObjects.find(o => o.id === id)     →  sceneObjects.get(id)
 *   sceneObjects.map(o => o.id === id ? patch : o)  →  patchSceneObject(map, id, patch)
 *   [...sceneObjects, newObj]               →  addSceneObject(map, obj).map
 *   sceneObjects.filter(o => o.id !== id)   →  removeSceneObject(map, id)
 *
 * `addSceneObject` and `addSceneObjects` enforce the global memory limit via
 * the `policy` argument. `user-add` (the default) is gated by the memory
 * governor: at the `over` level — or when the proposed allocation would push
 * the heap over the configured limit — the addition is refused, the map is
 * returned unchanged, and `admitted: false` is returned. `system` and `force`
 * always admit; use them for deterministic re-evaluation paths (generator
 * outputs, undo restore, scene-file load) where refusing would corrupt
 * invariants downstream.
 */

import type { SceneObject } from "../modeling/types.ts";
import { canAllocate, showMemoryToast } from "./memoryGovernor.ts";
import { estimateRetainedBytes } from "./storeUndo.ts";

export type SceneObjectMap = Map<string, SceneObject>;

/**
 * Memory-pressure policy for `addSceneObject` / `addSceneObjects`.
 *
 *   user-add — caused by an explicit user action (primitive, library spawn,
 *              import, duplicate, mesh-separate). Subject to the governor's
 *              gate; refused at `over`.
 *   system   — caused by a deterministic re-eval (generators, modifiers) or
 *              by undo/restore. Never refused — refusing would break
 *              determinism or strand selections pointing to nonexistent ids.
 *   force    — escape hatch (recovery, migration, in-process refactors that
 *              must always succeed). Functionally identical to `system` today;
 *              kept distinct so audit greps stay precise.
 */
export type AddPolicy = "user-add" | "system" | "force";

export interface AddSceneObjectResult {
  /** Resulting map. Equals the input map (by identity) when admission is denied. */
  map: SceneObjectMap;
  /** True when the object(s) were added; false when the gate refused. */
  admitted: boolean;
}

/** Create a new map with the object patched. Returns the same map if id not found. */
export function patchSceneObject(
  map: SceneObjectMap,
  id: string,
  patch: Partial<SceneObject>,
): SceneObjectMap {
  const obj = map.get(id);
  if (!obj) return map;
  const next = new Map(map);
  next.set(id, { ...obj, ...patch });
  return next;
}

/** Create a new map with a full replacement object. Returns the same map if id not found. */
export function replaceSceneObject(
  map: SceneObjectMap,
  id: string,
  replacement: SceneObject,
): SceneObjectMap {
  if (!map.has(id)) return map;
  const next = new Map(map);
  next.set(id, replacement);
  return next;
}

/** Create a new map with the object added, subject to the memory-governor gate. */
export function addSceneObject(
  map: SceneObjectMap,
  obj: SceneObject,
  policy: AddPolicy = "user-add",
): AddSceneObjectResult {
  if (policy === "user-add" && !admitOrToast([obj])) {
    return { map, admitted: false };
  }
  const next = new Map(map);
  next.set(obj.id, obj);
  return { map: next, admitted: true };
}

/** Create a new map with multiple objects added, subject to the memory-governor gate. */
export function addSceneObjects(
  map: SceneObjectMap,
  objs: readonly SceneObject[],
  policy: AddPolicy = "user-add",
): AddSceneObjectResult {
  if (objs.length === 0) return { map, admitted: true };
  if (policy === "user-add" && !admitOrToast(objs)) {
    return { map, admitted: false };
  }
  const next = new Map(map);
  for (const obj of objs) next.set(obj.id, obj);
  return { map: next, admitted: true };
}

/** Create a new map with the object removed. */
export function removeFromSceneMap(
  map: SceneObjectMap,
  id: string,
): SceneObjectMap {
  if (!map.has(id)) return map;
  const next = new Map(map);
  next.delete(id);
  return next;
}

/** Convert an array of SceneObjects to a Map (for migration/deserialization). */
export function sceneObjectsToMap(arr: SceneObject[]): SceneObjectMap {
  const map = new Map<string, SceneObject>();
  for (const obj of arr) map.set(obj.id, obj);
  return map;
}

// ---------------------------------------------------------------------------
// Gate helper
// ---------------------------------------------------------------------------

/**
 * Estimate the bytes the proposed add(s) would retain and consult the
 * memory governor. Fires a deduplicated error toast on rejection so
 * callers don't each have to surface their own message.
 */
function admitOrToast(objs: readonly SceneObject[]): boolean {
  let estBytes = 0;
  for (const obj of objs) estBytes += estimateRetainedBytes(obj);
  if (canAllocate(estBytes)) return true;
  showMemoryToast(
    "Cannot add geometry — memory limit reached. Remove objects, undo, or raise the limit in Settings.",
    {
      severity: "error",
      duration: 6000,
      dedupKey: "memory-add-refused",
    },
  );
  return false;
}
